import {
  Alert,
  Anchor,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import clsx from 'clsx';
import { useState } from 'react';
import { BuzzTransactionButton } from '~/components/Buzz/BuzzTransactionButton';
import { AspectRatioImageCard } from '~/components/CardTemplates/AspectRatioImageCard';
import { SHARED_ALLOWANCE_NOTE } from '~/shared/utils/placement';
import { useDialogContext } from '~/components/Dialog/DialogProvider';
import { useQueryImages } from '~/components/Image/image.utils';
import { remixSubmitPickerFilters } from '~/components/RemixGallery/remix-gallery.utils';
import { InViewLoader } from '~/components/InView/InViewLoader';
import { NoContent } from '~/components/NoContent/NoContent';
import {
  freeRefusalExplanation,
  freeRefusalOutcome,
  freeSubmissionOffer,
  submissionMethod,
} from '~/components/RemixGallery/remix-gallery.utils';
import { paidSubmissionOpen } from '~/shared/utils/remix-gallery';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useAvailableBuzz } from '~/components/Buzz/useAvailableBuzz';
import { showErrorNotification, showSuccessNotification } from '~/utils/notifications';
import { trpc } from '~/utils/trpc';

/**
 * Submitting one of your own images into someone's remix gallery.
 *
 * The picker only offers your own published images because that is what the
 * mutation accepts; showing anything else would be an invitation to a refusal.
 */
export function RemixGallerySubmitModal({ hostImageId }: { hostImageId: number }) {
  const dialog = useDialogContext();
  const currentUser = useCurrentUser();
  const utils = trpc.useUtils();
  const spendTypes = useAvailableBuzz();
  const [selected, setSelected] = useState<number | null>(null);
  /**
   * An explicit choice, or `null` for "whatever the default is". Kept apart from
   * the resolved method below so the default can reapply when the selection
   * changes — free eligibility is a property of the image, not of the gallery,
   * so a choice made about one remix must not carry to the next.
   */
  const [chosen, setChosen] = useState<'free' | 'paid' | null>(null);
  /**
   * Why a free submission was refused, when a re-read could explain it. Held
   * here rather than shown as an error notification because it is an ordinary
   * outcome with a next step — the paid option, already on screen.
   */
  const [freeRefusal, setFreeRefusal] = useState<string | null>(null);

  const { data: visibility, isError: visibilityFailed } =
    trpc.placement.getRemixGalleryVisibility.useQuery({ imageId: hostImageId });

  /**
   * Asked for the selected image alone rather than for the whole grid. The
   * verification is a per-row containment test, and the control only ever needs
   * the answer for the one image about to be submitted — probing every image the
   * picker has paged through would grow without bound for the sake of a state
   * nobody is looking at.
   */
  const { data: freeInfo } = trpc.placement.getRemixGalleryFreeEligibility.useQuery(
    { hostImageId, imageIds: selected != null ? [selected] : [] },
    { enabled: !!currentUser && selected != null }
  );

  const {
    images,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isRefetching,
    isError: imagesFailed,
  } = useQueryImages(remixSubmitPickerFilters(currentUser?.id), { enabled: !!currentUser });

  const price = visibility?.price ?? null;

  const takesFree = (visibility?.freeSlots ?? 0) > 0;

  // `null` rather than a number when the viewer may not be told how many of a
  // creator's free slots are currently held — signed out, gallery closed, or
  // their own image. Treated as "not on offer" rather than as zero, because zero
  // would render "all taken right now" about a count we were not given.
  const slotsHeldKnown = visibility?.freeSlotsRemaining != null;

  // Not `visibility.open`, which is `mode !== 'off'` and says nothing about
  // price. A gallery can take free submissions and refuse every paid one.
  //
  // ⚠️ An UNLOADED gallery is not a closed one. `visibility?.price` is undefined
  // while the query is in flight, and treating that as closed put the card on
  // "Submit for free" for a beat on every ordinary paid gallery before it
  // flipped. Unknown reads as open, which is the pre-existing behaviour and the
  // one that does not flash.
  const paidOpen = visibility ? paidSubmissionOpen(visibility.price) : true;

  const offer = freeSubmissionOffer({
    verified: selected != null && !!freeInfo?.verifiedImageIds.includes(selected),
    freeSlots: visibility?.freeSlots ?? 0,
    freeSlotsRemaining: visibility?.freeSlotsRemaining ?? 0,
    allowanceRemaining: freeInfo?.allowance.remaining ?? 0,
    usedHere: !!freeInfo?.usedHere,
    resetsAt: freeInfo?.allowance.resetsAt ?? new Date(),
    paidOpen,
  });
  const freeAvailable = offer.available && slotsHeldKnown;
  // Withheld while the answer is in flight, so the card says nothing rather than
  // briefly asserting a reason drawn from defaulted zeroes.
  const freeUnavailableReason =
    selected != null && freeInfo && slotsHeldKnown ? offer.reason : null;

  const method = submissionMethod(chosen, freeAvailable, paidOpen, takesFree);

  const submit = trpc.placement.submitToRemixGallery.useMutation({
    onSuccess: () => {
      showSuccessNotification({
        title: 'Submitted',
        message: "Your remix is waiting for the creator's review.",
      });
      utils.placement.invalidate();
      dialog.onClose();
    },
    onError: async (error) => {
      /**
       * A free submission can be refused for two very different kinds of reason,
       * and only one of them is an ordinary outcome.
       *
       * The free primitive re-counts under its lock, so losing the last slot —
       * or the allowance, or the never-twice rule — is a race the control cannot
       * win and should not report as an error. Everything else is a real refusal
       * the submitter needs to read: unverified, a duplicate entry, an
       * unpublished image, the content rule, a block.
       *
       * Told apart by re-reading rather than by matching the server's wording,
       * which is prose it is free to change. If the fresh numbers still say free
       * was on offer, the refusal was not about capacity and the message is
       * shown. Falls back to showing it if the re-read itself fails, because
       * silence is the one outcome that leaves nobody informed.
       */
      /**
       * 🔴 `staleTime: 0` on both fetches, and it is load-bearing rather than
       * decorative — the sticker sibling says the same thing at
       * `Sticker/placement.util.ts`. The client's global default is
       * `staleTime: Infinity`, and `fetchQuery` applies it: both keys below are
       * the mounted queries' own keys and both have data at click time, so
       * without this the "re-read" resolves the CACHE and never calls the
       * procedure.
       *
       * What that cost when it was missing is the whole point of this handler.
       * Pressing Submit for free requires `freeAvailable`, which means
       * `freeSubmissionOffer` over those cached inputs returned available — and
       * `freeRefusalExplanation` is that same function over those same inputs,
       * so it returned `null` every time. Every refused free submission fell to
       * the server branch and showed raw server prose, which is precisely the
       * behaviour the refusal split exists to remove.
       *
       * The FRESH `paidOpen` then travels out beside the explanation, because
       * the rendered one is not merely stale: with `staleTime: Infinity` and no
       * refetch on focus, a modal left open across a price change holds the
       * render value for the whole session.
       *
       * Using the stale flag here while the explanation beside it used the fresh
       * one produced the exact failure `paidOpen` was added to prevent: an owner
       * clearing their price mid-submission got "you can still submit with Buzz"
       * and a control moved to paid, then `submissionMethod` pulled it back to
       * free-disabled on the next render — a banner pointing at a button that is
       * not on the screen.
       */
      const refusal =
        method === 'free' && selected != null
          ? await utils.placement.getRemixGalleryVisibility
              .fetch({ imageId: hostImageId }, { staleTime: 0 })
              .then(async (space) => {
                const standing = await utils.placement.getRemixGalleryFreeEligibility.fetch(
                  { hostImageId, imageIds: [selected] },
                  { staleTime: 0 }
                );

                const freshPaidOpen = paidSubmissionOpen(space.price);
                return {
                  paidOpen: freshPaidOpen,
                  explained:
                    space.freeSlotsRemaining == null
                      ? null
                      : freeRefusalExplanation({
                          verified: standing.verifiedImageIds.includes(selected),
                          freeSlots: space.freeSlots,
                          freeSlotsRemaining: space.freeSlotsRemaining,
                          allowanceRemaining: standing.allowance.remaining,
                          usedHere: standing.usedHere,
                          resetsAt: standing.allowance.resetsAt,
                          paidOpen: freshPaidOpen,
                        }),
                };
              })
              // A real network read can reject, which is what `staleTime: 0`
              // reintroduces. A failed re-read explains nothing, so this lands on
              // the server's own message — the outcome that leaves somebody
              // informed. The `paidOpen` here is never read: `freeRefusalOutcome`
              // consults it only on the explained branch. Supplied so the shape
              // matches rather than because it decides anything.
              .catch(() => ({ paidOpen, explained: null }))
          : { paidOpen, explained: null };

      const explained = refusal.explained;
      const outcome = freeRefusalOutcome(explained, error.message, refusal.paidOpen);
      if (outcome.fallBackToPaid) setChosen('paid');

      // Keyed on whether we could explain it, NOT on whether we moved the
      // control. Those came apart once paid stopped being a guaranteed
      // alternative: an explained refusal on a gallery that takes no paid
      // submissions still has something worth saying, and routing it to the
      // toast instead would file an ordinary outcome as an error.
      setFreeRefusal(explained !== null ? outcome.message : null);
      if (explained === null)
        showErrorNotification({
          title: outcome.title,
          error: new Error(outcome.message),
        });
    },
  });

  // Filtered rather than shown-and-refused. Until the ceiling arrives nothing is
  // offered: rendering the whole grid first and removing images a moment later
  // invites a click on one that is about to disappear.
  const maxLevel = visibility?.maxSubmissionLevel;
  const eligible =
    maxLevel == null
      ? []
      : images.filter((image) => !!image.nsfwLevel && image.nsfwLevel <= maxLevel);
  // The picker's query backfills `browsingLevel`, which forces the
  // `nsfwLevel != 0` branch server-side, so an unrated image never arrives here
  // and every hidden one is genuinely over the ceiling.
  const overRated =
    maxLevel == null
      ? 0
      : images.filter((image) => !!image.nsfwLevel && image.nsfwLevel > maxLevel).length;

  return (
    // `padding={0}` would strip the header's padding too, putting the title and
    // the close button against the edges. Only the body needs to lose it, so the
    // scroll container can run edge to edge and own its own insets.
    <Modal
      {...dialog}
      // Spans, not a `div`/`Text` stack: Mantine renders the title inside an
      // `h2`, and block elements there are invalid nesting that React reparses.
      title={
        <span className="flex flex-col gap-0.5">
          <span>Submit your remix</span>
          <Text span size="xs" fw={400} c="dimmed" className="block leading-snug">
            The creator reviews every submission and decides what belongs in their gallery.
          </Text>
        </span>
      }
      size="lg"
      classNames={{ body: 'p-0', header: 'items-start pb-3' }}
    >
      {/* Three bands: the explanation and the actions stay put, only the picker
          scrolls. The fee warning is money copy, so it must not be the thing
          that scrolls out of sight while someone hunts for an image. */}
      <div className="flex max-h-[70vh] flex-col">
        <Stack gap="xs" className="shrink-0 px-4 pb-3 pt-0">
          {/* Footnotes on the title's sentence, not statements of equal weight —
              icon rows in the crypto deposit card's shape, so a stack of them
              reads as one block rather than as paragraphs. */}
          <Stack gap={4}>
            {/* The picker lists published images because that is all the mutation
                accepts, and a freshly generated image is not one. Said here rather
                than only in the empty state: the case that confuses people is
                having images in the grid and not the one they just made. */}
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <IconInfoCircle
                size={14}
                className="text-blue-5"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Text size="xs" c="dimmed">
                Only images you have posted appear below.{' '}
                <Anchor href="/posts/create" target="_blank" rel="noreferrer" size="xs">
                  Post your remix first
                </Anchor>{' '}
                if you don&apos;t see it.
              </Text>
            </Group>

            {/* Says why the grid is short. Without it a submitter whose work is
                mostly mature sees a picker missing their best images and no
                reason, which reads as a bug rather than a rule. The reason is not
                named: which rule bit is the host's business, not the
                submitter's. */}
            {maxLevel === 0 ? (
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <IconAlertTriangle
                  size={14}
                  className="text-yellow-500"
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <Text size="xs" c="dimmed">
                  This image has no rating yet, so nothing can be submitted to it.
                </Text>
              </Group>
            ) : (
              overRated > 0 && (
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <IconAlertTriangle
                    size={14}
                    className="text-yellow-500"
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <Text size="xs" c="dimmed">
                    {overRated === 1 ? '1 of your images is' : `${overRated} of your images are`}{' '}
                    rated above what this gallery accepts, so{' '}
                    {overRated === 1 ? 'it is' : 'they are'} not shown.
                  </Text>
                </Group>
              )
            )}

            {visibility && !visibility.open && (
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <IconAlertTriangle
                  size={14}
                  className="text-yellow-500"
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <Text size="xs" c="yellow">
                  This creator has stopped accepting submissions.
                </Text>
              </Group>
            )}
          </Stack>
        </Stack>

        <Divider />

        {/* The scroll container, and the only thing that pages. `InViewLoader`
            fires on viewport intersection, so it has to live inside here — it
            is reached by scrolling this box, not the page. */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {/* Waits on the ceiling as well as the images: without it the picker
              renders "you have nothing to submit" for a beat while the rule is
              still in flight. */}
          {visibilityFailed && !visibility ? (
            // An errored query settles with no data and isLoading false, so
            // waiting on `visibility` alone spins here forever with nothing to
            // read and nothing to press. Gated on `!visibility` as well, because
            // React Query keeps the previous data through a failed refetch — the
            // error alone would replace a working, already-populated picker.
            <Alert color="red" icon={<IconAlertTriangle />}>
              Couldn&apos;t load this gallery. Close this and try again.
            </Alert>
          ) : isLoading || !visibility ? (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          ) : eligible.length ? (
            <>
              <div className="grid grid-cols-4 gap-3">
                {eligible.map((image) => (
                  <AspectRatioImageCard
                    key={image.id}
                    aspectRatio="square"
                    image={image}
                    onClick={() => {
                      setSelected(image.id);
                      // Free eligibility is a property of the image, so a choice
                      // made about one remix must not carry to the next.
                      setChosen(null);
                      setFreeRefusal(null);
                    }}
                    className={clsx(
                      'cursor-pointer',
                      selected === image.id && 'ring-2 ring-blue-5'
                    )}
                  />
                ))}
              </div>

              {/* Scroll-to-load, which is what a populated grid should do.

                  `!imagesFailed` is load-bearing: `hasNextPage` is derived from the last
                  SUCCESSFUL page, so it stays true after a failed fetch and this loader
                  would otherwise stay mounted in view and re-fire every ~500ms with no
                  backoff. The feed proper swaps in SearchRetryBanner on error; this
                  surface has no such affordance, so it must simply stop. */}
              {hasNextPage && (
                <InViewLoader loadFn={fetchNextPage} loadCondition={!isRefetching && !imagesFailed}>
                  <Group justify="center" py="md">
                    <Loader size="sm" />
                  </Group>
                </InViewLoader>
              )}
            </>
          ) : (
            <NoContent
              message={
                maxLevel == null
                  ? // Three different reasons collapse into a null ceiling, and
                    // naming the wrong one is worse than saying less: signed
                    // out, gallery closed, or a host that cannot show a gallery
                    // at all (mid-rescan, blocked, under review).
                    !currentUser
                    ? 'Sign in to submit a remix to this gallery.'
                    : visibility?.open
                    ? 'This image cannot show a gallery right now.'
                    : 'This gallery is not open for submissions.'
                  : maxLevel === 0
                  ? 'This image has no rating yet, so nothing can be submitted to it.'
                  : hasNextPage
                  ? // Hedged while pages remain, because every count here is
                    // computed from what has loaded. Phrased as a paused state
                    // rather than an active one: nothing is fetching now, the
                    // button below is the action.
                    'Nothing so far in the images loaded.'
                  : overRated > 0
                  ? 'None of your posted images are rated low enough for this gallery.'
                  : "You don't have any posted images to submit yet. Post your remix first, then submit it here."
              }
            />
          )}

          {/* A button rather than the in-view loader used above. With nothing
              eligible there is no content between the loader and the top of the
              scroll box, so it would sit permanently in view: fire, load 50,
              still be in view, fire again — walking the whole library in a burst
              of requests, in exactly the case where nothing will be found.
              Paging past the first page is worth doing, but on intent. */}
          {!eligible.length && hasNextPage && !isLoading && visibility && (
            <Group justify="center" mt={-12} pb="sm">
              <Button
                variant="subtle"
                size="compact-sm"
                loading={isRefetching}
                onClick={() => fetchNextPage()}
              >
                Keep looking through your images
              </Button>
            </Group>
          )}
        </div>

        <Divider />

        {/* The free/paid choice, and the last moment it can be made. It carries
            the mode as well as the price because those are two different offers
            and the placer is about to spend something either way — a free
            submission still costs them their daily allowance, decline or not.
            Hidden entirely when the gallery takes no free submissions at all: a
            control with one reachable answer is not a choice. */}
        {/* 🔴 Outside the block below, and that is the point rather than layout.
            The choice is hidden when the creator takes no free submissions —
            and `freeSlots <= 0` is ALSO one of the rungs that can explain a
            refusal, when a creator closes their free capacity mid-modal. Nested,
            the same fresh read that produces this message unmounts the thing
            rendering it: the submission fails, the toast is suppressed because
            the refusal was explained, and the only visible change is the control
            quietly turning into a Buzz button. That is the same shape as the bug
            this message exists to fix — written for somebody, shown to nobody. */}
        {freeRefusal && (
          <>
            <Divider />
            <Group gap="xs" wrap="nowrap" align="flex-start" className="shrink-0 px-4 pt-3">
              <IconAlertTriangle
                size={14}
                className="text-yellow-500"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Text size="xs" c="yellow">
                {freeRefusal}
              </Text>
            </Group>
          </>
        )}

        {selected != null && takesFree && (
          <>
            <Divider />
            <Stack gap={6} className="shrink-0 px-4 pt-3">
              <SegmentedControl
                value={method}
                onChange={(value) => setChosen(value as 'free' | 'paid')}
                data={[
                  {
                    value: 'free',
                    // 🔴 Just "Free". It read `Free · needs review` beside a
                    // plain `N Buzz`, which says the paid one does not — and on
                    // this surface EVERY submission goes to the creator, free or
                    // paid, because a gallery places arbitrary media on someone
                    // else's page and `auto` is refused for it in three places.
                    // The review is said once, below, where it applies to both.
                    label: 'Free',
                    disabled: !freeAvailable,
                  },
                  {
                    value: 'paid',
                    label: paidOpen ? `${price} Buzz` : 'With Buzz',
                    // Not `price == null`: a gallery priced below the surface
                    // floor refuses paid submissions too, and offering that
                    // option leads into a second refusal rather than a disabled
                    // button. Same predicate as the copy, one layer down —
                    // `paidOpen` reaching only the sentence is how this shipped
                    // wrong once already.
                    disabled: !paidOpen,
                  },
                ]}
              />

              {/* No number and no reset time written here. The allowance is a
                  server-side rule and both of its facts already come back from
                  `getFreePlacementAllowance`; a sentence that spells either one
                  out is a claim this file cannot keep true.

                  What the allowance is SHARED with is not such a claim — it is a
                  product rule, it is the one readers were getting wrong, and it
                  comes from the same constant the sticker surface uses so the
                  two cannot describe different budgets. */}
              {method === 'free' && freeAvailable && (
                <Text size="xs" c="dimmed">
                  This spends your free placement for today &mdash; {SHARED_ALLOWANCE_NOTE} &mdash;
                  and it is spent even if the creator declines.
                </Text>
              )}
            </Stack>
          </>
        )}

        {/* Rendered AFTER the segmented control so it reads as a note on the
            greyed-out Free button rather than a preamble to the choice, but
            still gated independently of it.

            🔴 That independence is the point rather than the order. Every gate
            this sentence has lived behind has hidden it in the state that needed
            it most: `method === 'paid'` hid it once the card started holding on
            free, and `takesFree` hides it on a gallery that takes no free
            submissions and no paid ones either — a dead end with no explanation.
            Moving it inside the block above would reintroduce exactly that.
            `offer.reason` is null whenever free is genuinely on offer, so this
            cannot fire against a working FREE button. It can and should fire
            beside a working PAID one — an unverified remix on an
            ordinarily-priced gallery is the commonest case there is, and the
            sentence is the only thing explaining why free is greyed out. */}
        {selected != null && !freeRefusal && freeUnavailableReason && (
          <>
            {/* Only when the control above is absent. With it present this note
                is a caption on it, and a rule between the two reads as a section
                break where there is no new section. */}
            {!takesFree && <Divider />}
            <Group gap="xs" wrap="nowrap" align="flex-start" className="shrink-0 px-4 pt-3">
              <IconInfoCircle
                size={14}
                className="text-blue-500"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Text size="xs" c="dimmed">
                {freeUnavailableReason}
              </Text>
            </Group>
          </>
        )}

        <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3">
          {/* The paid path's own warning, and paid-only: a free submission puts
              nothing in escrow, so there is no decline fee and quoting one would
              describe money that never moved. The amount is the server's own
              figure, computed with the helper the refund uses from the
              operator-set rate — "30%" written here would be a number this file
              cannot keep true. */}
          {method === 'paid' && visibility?.declineFee ? (
            // `min-w-0` is what makes the note wrap instead of pushing: a flex
            // item's floor is its content width otherwise, so a long sentence
            // squeezed the Submit button until its label clipped.
            <Group gap="xs" wrap="nowrap" align="flex-start" className="min-w-0 flex-1">
              <IconAlertTriangle
                size={14}
                className="text-yellow-500"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Text size="xs" c="dimmed">
                If your remix is declined, the creator keeps {visibility.declineFee} Buzz and the
                rest comes back.
              </Text>
            </Group>
          ) : (
            <span className="flex-1" />
          )}
          <div className="shrink-0">
            {/* A plain button on the free path. `BuzzTransactionButton` exists to
              check a balance and open the top-up flow, and running a free
              submission through it would ask someone to have Buzz they are not
              about to spend. */}
            {method === 'free' ? (
              <Button
                disabled={!selected || !visibility?.open || !freeAvailable}
                loading={submit.isPending}
                onClick={() =>
                  selected != null && submit.mutate({ hostImageId, imageId: selected, free: true })
                }
              >
                Submit for free
              </Button>
            ) : (
              /* The price shown here is the one the balance check runs against, and
              the owner can move it between this render and the click. The
              mutation reads the price fresh, so the button is honest about
              affordability but cannot promise the amount — hence the note above
              it rather than a silent charge. */
              <BuzzTransactionButton
                buzzAmount={price ?? 0}
                // Says what it means. `BuzzTransactionButton` already ran the pair
                // through `useAvailableBuzz`, which strips both and appends the
                // domain's, so this renders identically — the pair was never the
                // hole. That was server-side, where the escrow drew from both.
                accountTypes={spendTypes}
                label="Submit"
                disabled={!selected || !visibility?.open || !paidOpen}
                loading={submit.isPending}
                // The price this render displayed travels with the submission, so
                // the server refuses rather than charging a number the submitter
                // never agreed to. Affordability was checked against this one too.
                onPerformTransaction={() =>
                  selected != null &&
                  price != null &&
                  submit.mutate({ hostImageId, imageId: selected, expectedPrice: price })
                }
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
